// ---------------------------------------------------------------------------
// The queue of sales the server has not got yet.
//
// This screen exists so that "there are three sales waiting" is never a
// mystery. A cashier can see which sales, for how much, and - when one has
// been refused - exactly why, in a sentence rather than an error code.
//
// The discard button is the only way a queued sale leaves without the server
// confirming it. It is deliberately awkward: it asks first, it says what will
// be lost, and it is the last resort for a sale that can genuinely never be
// recorded. Everything else on this screen pushes towards retrying.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { CloudOff, RefreshCw, Trash2, TriangleAlert } from 'lucide-react';
import clsx from 'clsx';
import { useI18n } from '../i18n/I18nProvider';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { isEffectivelyOffline, useOfflineStore } from '../stores/offlineStore';
import { refreshQueue, retryAllFailed, retrySale, syncNow } from '../lib/syncEngine';
import { discardSale } from '../lib/offlineDb';
import { EmptyState } from '../components/common/StateBlocks';
import { formatDateTime, formatUsd } from '../utils/format';

export function OfflineQueuePage() {
  const { t, locale } = useI18n();
  useDocumentTitle(t.offline.queueTitle);

  const pending = useOfflineStore((state) => state.pending);
  const phase = useOfflineStore((state) => state.phase);
  const lastSync = useOfflineStore((state) => state.lastSync);
  const lastError = useOfflineStore((state) => state.lastError);
  const browserOnline = useOfflineStore((state) => state.browserOnline);
  const serverReachable = useOfflineStore((state) => state.serverReachable);

  const [confirming, setConfirming] = useState<string | null>(null);

  const offline = isEffectivelyOffline({ browserOnline, serverReachable });
  const failed = pending.filter((sale) => sale.failedReason !== undefined);

  useEffect(() => {
    void refreshQueue();
  }, []);

  return (
    <div className="mx-auto max-w-4xl space-y-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">{t.offline.queueTitle}</h2>
          <p className="mt-1 text-sm text-ink-muted">{t.offline.queueSubtitle}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="btn-secondary gap-1.5"
            disabled={phase === 'syncing' || pending.length === 0}
            onClick={() => void syncNow({ manual: true })}
          >
            <RefreshCw
              className={clsx('h-4 w-4', phase === 'syncing' && 'animate-spin')}
              aria-hidden="true"
            />
            {phase === 'syncing' ? t.offline.syncing : t.offline.syncNow}
          </button>

          {failed.length > 0 ? (
            <button type="button" className="btn-primary" onClick={() => void retryAllFailed()}>
              {t.offline.retryAll.replace('{count}', String(failed.length))}
            </button>
          ) : null}
        </div>
      </div>

      {/* --- where things stand ---------------------------------------- */}
      <section
        className={clsx(
          'card border-s-4 p-4',
          offline ? 'border-s-antique-gold' : 'border-s-butcher-green',
        )}
      >
        <div className="flex items-start gap-3">
          <CloudOff
            className={clsx('h-5 w-5 shrink-0', offline ? 'text-antique-gold' : 'text-butcher-green')}
            aria-hidden="true"
          />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-ink">
              {offline ? t.offline.offlineDetail : t.offline.online}
            </p>
            <p className="mt-0.5 text-sm text-ink-muted">
              {pending.length === 0
                ? t.offline.allHandedOver
                : t.offline.waiting.replace('{count}', String(pending.length))}
            </p>

            {lastSync ? (
              <p className="mt-1 text-xs text-ink-muted">
                {t.offline.lastSync
                  .replace('{when}', formatDateTime(lastSync.at, locale))
                  .replace('{synced}', String(lastSync.synced))
                  .replace('{duplicates}', String(lastSync.duplicates))}
              </p>
            ) : null}

            {lastError ? <p className="mt-1 text-xs text-danger">{lastError}</p> : null}
          </div>
        </div>
      </section>

      {/* Nothing is ever lost by being queued: it is on the device, and the
          device is what the cashier is holding. Worth saying plainly. */}
      <p className="card-soft px-4 py-3 text-sm text-ink-muted">{t.offline.safetyNote}</p>

      {/* --- the queue ------------------------------------------------- */}
      {pending.length === 0 ? (
        <div className="card">
          <EmptyState title={t.offline.emptyQueue} description={t.offline.emptyQueueHint} />
        </div>
      ) : (
        <section className="card overflow-hidden">
          <ul className="divide-y divide-line">
            {pending.map((sale) => (
              <li key={sale.offlineId} className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">
                      {formatUsd(sale.totalUsd)}
                      <span className="ms-2 text-xs font-normal text-ink-muted">
                        {t.offline.itemCount.replace('{count}', String(sale.itemCount))}
                      </span>
                    </p>
                    <p className="mt-0.5 text-xs text-ink-muted">
                      {formatDateTime(sale.createdAt, locale)}
                    </p>
                    <p className="mt-0.5 font-mono text-[11px] text-ink-muted/70" dir="ltr">
                      {sale.offlineId}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    {sale.failedReason ? (
                      <>
                        <button
                          type="button"
                          className="btn-ghost text-xs"
                          onClick={() => void retrySale(sale.offlineId)}
                        >
                          {t.offline.retry}
                        </button>
                        <button
                          type="button"
                          className="rounded-control p-1.5 text-ink-muted transition-colors hover:bg-danger/10 hover:text-danger"
                          title={t.offline.discard}
                          onClick={() => setConfirming(sale.offlineId)}
                        >
                          <Trash2 className="h-4 w-4" aria-hidden="true" />
                          <span className="sr-only">{t.offline.discard}</span>
                        </button>
                      </>
                    ) : (
                      <span className="text-xs text-ink-muted">{t.offline.waitingItem}</span>
                    )}
                  </div>
                </div>

                {sale.failedReason ? (
                  <div className="mt-2 flex items-start gap-2 rounded-control bg-danger/10 px-3 py-2">
                    <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-danger" aria-hidden="true" />
                    <div className="min-w-0">
                      <p className="text-sm text-danger">{sale.failedReason}</p>
                      <p className="mt-0.5 text-xs text-ink-muted">
                        {t.offline.attempts.replace('{count}', String(sale.attempts))}
                      </p>
                    </div>
                  </div>
                ) : null}

                {/* Discarding is the only way a sale leaves without the server
                    having it, so it asks first and says what that means. */}
                {confirming === sale.offlineId ? (
                  <div className="mt-2 rounded-control border border-danger/40 bg-danger/5 p-3">
                    <p className="text-sm font-medium text-ink">{t.offline.discardConfirm}</p>
                    <p className="mt-0.5 text-xs text-ink-muted">{t.offline.discardWarning}</p>

                    <div className="mt-3 flex gap-2">
                      <button
                        type="button"
                        className="btn-danger text-xs"
                        onClick={() => {
                          void discardSale(sale.offlineId).then(() => {
                            setConfirming(null);
                            return refreshQueue();
                          });
                        }}
                      >
                        {t.offline.discardYes}
                      </button>
                      <button
                        type="button"
                        className="btn-ghost text-xs"
                        onClick={() => setConfirming(null)}
                      >
                        {t.common.cancel}
                      </button>
                    </div>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
